import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  IssueAdditionalItemsTable,
  type IssueAdditionalItemRow,
  type IssueAdditionalItemDisplayRow,
} from './IssueAdditionalItemsTable';
import type { IssueAdditionalCartItem } from './types';
import type { LocationOption } from './IssuePlannerTable';
import { useAdditionalDbCartSync } from './useDbCartSync';
import { useAssetAllocator, useReservedAssets } from './useAssetLogic';
import {
  buildAdditionalItemsDisplayRows,
  type AvailabilityFilter,
} from './additionalItemsHierarchy';
import {
  useAllCatalogChildren,
  useIEMAdditionalItemGroups,
} from '@/hooks/queries/iem/useIEMQueries';
import { useHierarchyEntities } from '@/hooks/queries/entities/useHierarchyEntities';
import { useNotifications } from '@/stores/useNotificationStore';
import { useLocationCategoryLabels } from '@/hooks/useLocationCategoryLabels';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { httpClient } from '@/utils/httpClient';
import { iemService } from '@/services/iem';

export interface IssueAdditionalItemsTabOptions {
  open: boolean;
  active?: boolean;
  customerId?: string;
  externalReservedAssetIds: string[];
  onReservedAssetIdsChange?: (ids: string[]) => void;
  readOnly?: boolean;
}

export interface IssueAdditionalItemsTabActions {
  adjustQuantity: (groupId: string, delta: number) => void;
  removeItem: (groupId: string) => void;
  clearCart: () => void;
}

export interface IssueAdditionalItemsTabResult {
  toolbar: ReactNode | null;
  content: ReactNode;
  cartItems: IssueAdditionalCartItem[];
  availabilityMap: Record<string, number>;
  actions: IssueAdditionalItemsTabActions;
  reservedAssetIds: string[];
}

export const useIssueAdditionalItemsTab = ({
  open,
  active = true,
  customerId,
  externalReservedAssetIds,
  onReservedAssetIdsChange,
  readOnly = false,
}: IssueAdditionalItemsTabOptions): IssueAdditionalItemsTabResult => {
  const isTabActive = open && active;
  const [hasActivatedOnce, setHasActivatedOnce] = useState(false);
  const isDataEnabled = open && (active || hasActivatedOnce);

  useEffect(() => {
    if (isTabActive) {
      setHasActivatedOnce(true);
    }
  }, [isTabActive]);

  useEffect(() => {
    if (!open) {
      setHasActivatedOnce(false);
    }
  }, [open]);

  const notifications = useNotifications();
  const { primaryLabel, secondaryLabel, relatedLabel } = useLocationCategoryLabels();
  const {
    data: additionalGroupData = [],
    isLoading,
    isLoadingMore,
    refetch: refetchAdditional,
  } = useIEMAdditionalItemGroups({
    enabled: isDataEnabled,
  });

  // Database cart sync - filters by item_source='additional' automatically
  const dbCart = useAdditionalDbCartSync(customerId, open);
  const additionalItems = dbCart.cartItems;

  const { createReservedAssetSet, reservedAssetIds } = useReservedAssets({
    externalReservedAssetIds,
    cartReservedAssetIds: dbCart.reservedAssetIds,
  });

  const { allocateAssets } = useAssetAllocator({
    dbCart,
    createReservedAssetSet,
    refreshAvailability: () => void refetchAdditional(),
  });

  const { data: allChildren = [], isLoading: isCatalogChildrenLoading } = useAllCatalogChildren({
    enabled: isDataEnabled,
  });

  const parentIdsWithChildren = useMemo(() => {
    const parents = new Set<string>();
    allChildren.forEach((child) => {
      if (child.parentCatalogId) {
        parents.add(child.parentCatalogId);
      }
    });
    return parents;
  }, [allChildren]);

  // Collect unique catalog IDs (both direct group IDs and parent IDs) to fetch locations
  const additionalCatalogIds = useMemo(() => {
    const ids = new Set<string>();
    additionalGroupData.forEach((group) => {
      if (group.groupId) ids.add(group.groupId);
    });
    parentIdsWithChildren.forEach((id) => ids.add(id));
    return Array.from(ids);
  }, [additionalGroupData, parentIdsWithChildren]);

  // Fetch catalog entities in batches to avoid URL length limits
  const { data: allCatalogEntities = [], isLoading: isLocationsLoading } = useQuery({
    queryKey: ['catalog_entities_for_locations', 'issue_additional', additionalCatalogIds],
    queryFn: async () => {
      if (additionalCatalogIds.length === 0) return [];
      const BATCH_SIZE = 80;
      const batches: string[][] = [];
      for (let i = 0; i < additionalCatalogIds.length; i += BATCH_SIZE) {
        batches.push(additionalCatalogIds.slice(i, i + BATCH_SIZE));
      }
      const results = await Promise.all(
        batches.map((batch) => {
          const filter = batch.length === 1 ? `eq.${batch[0]}` : `in.(${batch.join(',')})`;
          return httpClient.getAll<{ id: string; entity_data: Record<string, unknown> | null }>(
            `/api/v1/entities?entity_type=eq.catalog&is_current=eq.true&id=${filter}&select=id,entity_data`,
          );
        }),
      );
      return results.flat();
    },
    staleTime: 60_000,
    enabled: isDataEnabled && additionalCatalogIds.length > 0,
  });

  // Build map of catalogId → primary location (primary_location_id)
  const locationIdByCatalogId = useMemo(() => {
    const map = new Map<string, string>();
    allCatalogEntities.forEach((entity) => {
      const data = (entity.entity_data ?? {}) as Record<string, unknown>;
      const storefrontLocId =
        typeof data.primary_location_id === 'string' ? data.primary_location_id.trim() : null;
      const warehouseLocId =
        typeof data.secondary_location_id === 'string' ? data.secondary_location_id.trim() : null;
      const locationId = storefrontLocId || warehouseLocId;
      if (locationId && entity.id) {
        map.set(entity.id, locationId);
      }
    });
    // Inherit parent location for children that don't have their own
    allChildren.forEach((child) => {
      const parentId = child.parentCatalogId?.trim();
      const childId = child.id?.trim();
      if (parentId && childId && map.has(parentId) && !map.has(childId)) {
        map.set(childId, map.get(parentId)!);
      }
    });
    return map;
  }, [allCatalogEntities, allChildren]);

  // Build primary/secondary location maps (parents + children)
  const primaryLocationByCatalogId = useMemo(() => {
    const map = new Map<string, string>();
    allCatalogEntities.forEach((entity) => {
      const data = (entity.entity_data ?? {}) as Record<string, unknown>;
      const locId =
        typeof data.primary_location_id === 'string' ? data.primary_location_id.trim() : null;
      if (locId && entity.id) map.set(entity.id, locId);
    });
    // Inherit from parent for children without their own storefront
    allChildren.forEach((child) => {
      const parentId = child.parentCatalogId?.trim();
      const childId = child.id?.trim();
      if (parentId && childId && map.has(parentId) && !map.has(childId)) map.set(childId, map.get(parentId)!);
    });
    return map;
  }, [allCatalogEntities, allChildren]);

  const secondaryLocationByCatalogId = useMemo(() => {
    const map = new Map<string, string>();
    allCatalogEntities.forEach((entity) => {
      const data = (entity.entity_data ?? {}) as Record<string, unknown>;
      const locId =
        typeof data.secondary_location_id === 'string' ? data.secondary_location_id.trim() : null;
      if (locId && entity.id) map.set(entity.id, locId);
    });
    // Inherit from parent for children without their own warehouse
    allChildren.forEach((child) => {
      const parentId = child.parentCatalogId?.trim();
      const childId = child.id?.trim();
      if (parentId && childId && map.has(parentId) && !map.has(childId)) map.set(childId, map.get(parentId)!);
    });
    return map;
  }, [allCatalogEntities, allChildren]);

  // Combined parent + child catalog IDs for barcode_mappings lookup
  const allAdditionalCatalogIds = useMemo(() => {
    const ids = new Set(additionalCatalogIds);
    allChildren.forEach((child) => {
      if (child.id) ids.add(child.id);
    });
    return Array.from(ids);
  }, [additionalCatalogIds, allChildren]);

  // Catalog IDs for location availability. Keep both parent and child IDs so
  // parent rows can surface their full location counts in the selector.
  const locationCatalogIds = useMemo(() => {
    const ids = new Set<string>();
    additionalCatalogIds.forEach((id) => ids.add(id));
    allChildren.forEach((child) => {
      if (child.id) ids.add(child.id);
    });
    return Array.from(ids);
  }, [additionalCatalogIds, allChildren]);

  // Fetch barcode_mappings for related locations (batched to avoid URL length limits)
  const { data: barcodeMappings = [] } = useQuery({
    queryKey: ['barcode_mappings', 'issue_additional', allAdditionalCatalogIds],
    queryFn: async () => {
      if (allAdditionalCatalogIds.length === 0) return [];
      const BATCH_SIZE = 80;
      const batches: string[][] = [];
      for (let i = 0; i < allAdditionalCatalogIds.length; i += BATCH_SIZE) {
        batches.push(allAdditionalCatalogIds.slice(i, i + BATCH_SIZE));
      }
      const results = await Promise.all(
        batches.map((batch) => {
          const filter = batch.length === 1 ? `eq.${batch[0]}` : `in.(${batch.join(',')})`;
          return httpClient.getAll<{ catalog_id: string; location_id: string }>(
            `/api/v1/barcode_mappings?catalog_id=${filter}&select=catalog_id,location_id`,
          );
        }),
      );
      return results.flat();
    },
    staleTime: 60_000,
    enabled: isDataEnabled && allAdditionalCatalogIds.length > 0,
  });

  const relatedLocationsByCatalogId = useMemo(() => {
    const map = new Map<string, string[]>();
    barcodeMappings.forEach(({ catalog_id, location_id }) => {
      const existing = map.get(catalog_id) ?? [];
      if (!existing.includes(location_id)) existing.push(location_id);
      map.set(catalog_id, existing);
    });
    return map;
  }, [barcodeMappings]);

  // Fetch location hierarchy for name resolution
  const locationHierarchyQuery = useHierarchyEntities({
    dataSource: {
      endpoint: '/api/v1/location_hierarchy',
      order: 'breadcrumb.asc',
    },
    filters: {
      select: 'id,display_name,entity_identifier,breadcrumb,breadcrumb_names',
    },
    enabled: isDataEnabled,
  });

  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    (locationHierarchyQuery.data ?? []).forEach((loc) => {
      if (loc.id && loc.display_name) map.set(loc.id, loc.display_name);
    });
    return map;
  }, [locationHierarchyQuery.data]);

  const locationDropdownLabelById = useMemo(() => {
    const map = new Map<string, string>();
    (locationHierarchyQuery.data ?? []).forEach((loc) => {
      if (!loc.id) {
        return;
      }

      const breadcrumbNames = Array.isArray(loc.breadcrumb_names)
        ? loc.breadcrumb_names.filter(
            (value): value is string => typeof value === 'string' && value.trim().length > 0,
          )
        : [];

      const templateLevels = breadcrumbNames.slice(1, 3);
      const label = templateLevels.join(' / ').trim();
      map.set(loc.id, label || loc.display_name || loc.id);
    });
    return map;
  }, [locationHierarchyQuery.data]);

  const additionalItemsWithLocation = useMemo(() => {
    return additionalItems.map((item) => {
      const locationIds = item.sourceLocationIds ?? [];
      let sourceLocationLabel: string | null = null;
      if (locationIds.length === 1) {
        const locationId = locationIds[0];
        sourceLocationLabel = locationId
          ? (locationNameById.get(locationId) ?? locationId)
          : null;
      } else if (locationIds.length > 1) {
        sourceLocationLabel = 'Multiple locations';
      }

      return {
        ...item,
        sourceLocationLabel,
      };
    });
  }, [additionalItems, locationNameById]);

  // Fetch available asset counts per (catalog_id, location_id)
  const { data: locationAvailabilityData = { located: new Map(), unlocated: new Map() } } = useQuery({
    queryKey: ['location_availability', 'issue_additional', locationCatalogIds],
    queryFn: async () => {
      if (locationCatalogIds.length === 0) return { located: new Map<string, Map<string, number>>(), unlocated: new Map<string, number>() };
      const counts: Record<string, Record<string, number>> = await iemService.getAvailableAssetCountsByCatalogIds({
        catalogIds: locationCatalogIds,
      });
      // Convert { catalogId: { locationId: count } } to Map<string, Map<string, number>>
      const located = new Map<string, Map<string, number>>();
      const unlocated = new Map<string, number>();
      for (const [catalogId, locationCounts] of Object.entries(counts)) {
        const locMap = new Map<string, number>();
        for (const [locationId, count] of Object.entries(locationCounts as Record<string, number>)) {
          if (locationId === '_total') {
            if (count > 0) unlocated.set(catalogId, count);
          } else {
            locMap.set(locationId, count);
          }
        }
        if (locMap.size > 0) located.set(catalogId, locMap);
      }
      return { located, unlocated };
    },
    staleTime: 30_000,
    enabled: isDataEnabled && locationCatalogIds.length > 0,
  });

  const locationAvailability = locationAvailabilityData.located;

  // Build grouped location options per catalog ID (parents + children)
  const locationOptionsByCatalogId = useMemo(() => {
    const map = new Map<string, LocationOption[]>();

    const buildOptions = (catalogId: string, parentCatalogId?: string) => {
      const options: LocationOption[] = [];
      const primaryId = primaryLocationByCatalogId.get(catalogId) ?? (parentCatalogId ? primaryLocationByCatalogId.get(parentCatalogId) : undefined);
      const secondaryId = secondaryLocationByCatalogId.get(catalogId) ?? (parentCatalogId ? secondaryLocationByCatalogId.get(parentCatalogId) : undefined);
      const usedIds = new Set<string>();
      const locCounts = locationAvailability.get(catalogId);

      if (primaryId) {
        options.push({ id: primaryId, label: locationDropdownLabelById.get(primaryId) ?? primaryId, group: primaryLabel, qty: locCounts?.get(primaryId) ?? 0 });
        usedIds.add(primaryId);
      }
      if (secondaryId && !usedIds.has(secondaryId)) {
        options.push({ id: secondaryId, label: locationDropdownLabelById.get(secondaryId) ?? secondaryId, group: secondaryLabel, qty: locCounts?.get(secondaryId) ?? 0 });
        usedIds.add(secondaryId);
      }
      const related = relatedLocationsByCatalogId.get(catalogId) ?? [];
      related.forEach((locId) => {
        if (!usedIds.has(locId)) {
          options.push({ id: locId, label: locationDropdownLabelById.get(locId) ?? locId, group: relatedLabel, qty: locCounts?.get(locId) ?? 0 });
          usedIds.add(locId);
        }
      });

      // Add any locations with available assets that aren't already primary/secondary/related
      if (locCounts) {
        locCounts.forEach((count: number, locId: string) => {
          if (!usedIds.has(locId) && count > 0) {
            const name = locationDropdownLabelById.get(locId) ?? locId;
            options.push({ id: locId, label: name, group: relatedLabel, qty: count });
            usedIds.add(locId);
          }
        });
      }

      map.set(catalogId, options);
    };

    // Build for parent catalog IDs
    additionalCatalogIds.forEach((catalogId) => buildOptions(catalogId));

    // Build for child catalog IDs (size variants)
    allChildren.forEach((child) => {
      if (child.id && !map.has(child.id)) {
        buildOptions(child.id, child.parentCatalogId);
      }
    });

    return map;
  }, [additionalCatalogIds, allChildren, primaryLocationByCatalogId, secondaryLocationByCatalogId, relatedLocationsByCatalogId, locationDropdownLabelById, locationAvailability, primaryLabel, secondaryLabel, relatedLabel]);

  // Per-item selected location state (defaults to primary)
  const [selectedLocationByGroupId, setSelectedLocationByGroupId] = useState<Record<string, string | undefined>>({});

  const handleLocationChange = useCallback((groupId: string, locationId: string) => {
    setSelectedLocationByGroupId((prev) => ({ ...prev, [groupId]: locationId }));
  }, []);

  // Resolve effective location for allocation
  const resolveLocationForGroupId = useCallback((groupId: string): string | undefined => {
    const selectedId = selectedLocationByGroupId[groupId];
    if (selectedId) return selectedId;
    // Default to the first option with stock (mirrors the table's smart default)
    const options = locationOptionsByCatalogId.get(groupId) ?? [];
    const defaultOption = options.find((o) => (o.qty ?? 0) > 0);
    if (defaultOption) {
      return defaultOption.id;
    }
    return primaryLocationByCatalogId.get(groupId) ?? locationIdByCatalogId.get(groupId);
  }, [selectedLocationByGroupId, locationOptionsByCatalogId, primaryLocationByCatalogId, locationIdByCatalogId]);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [availabilityFilter, setAvailabilityFilter] = useState<AvailabilityFilter>('available');

  const [expandedParentIds, setExpandedParentIds] = useState<Set<string>>(() => new Set());

  const sizeLabelByCatalogId = useMemo(() => {
    const map = new Map<string, string>();
    allChildren.forEach((child) => {
      if (child.sizeLabel) {
        map.set(child.id, child.sizeLabel);
      }
    });
    return map;
  }, [allChildren]);

  useEffect(() => {
    if (!open) {
      // Don't clear cart on close - it persists in the database
      setSearch('');
      setAvailabilityFilter('available');
      setExpandedParentIds(new Set());
      setSelectedLocationByGroupId({});
    }
  }, [open]);

  const rowsByCatalogId = useMemo(() => {
    const reservedAssetIdsSet = createReservedAssetSet();

    const map = new Map<string, IssueAdditionalItemRow>();

    additionalGroupData.forEach((group) => {
      const availableAssetIds = group.assetIds.filter(
        (assetId) => !reservedAssetIdsSet.has(assetId),
      );

      let name = group.name;
      let sizeLabel: string | null = null;
      const sizeMatch = name.match(/^(.*)\s+\(([^)]+)\)$/);
      if (sizeMatch) {
        name = sizeMatch[1]!.trim();
        sizeLabel = sizeMatch[2]!.trim();
      }

      const catalogSizeLabel = sizeLabelByCatalogId.get(group.groupId);
      if (catalogSizeLabel) {
        sizeLabel = catalogSizeLabel;
      }

      map.set(group.groupId, {
        groupId: group.groupId,
        lin: group.lin,
        nsn: group.nsn,
        name,
        sizeLabel,
        availableQty: availableAssetIds.length,
        availableAssetIds,
      });
    });

    return map;
  }, [additionalGroupData, createReservedAssetSet, sizeLabelByCatalogId]);

  const addableItemRows = useMemo<IssueAdditionalItemRow[]>(() => {
    return Array.from(rowsByCatalogId.values()).filter(
      (row) => !parentIdsWithChildren.has(row.groupId),
    );
  }, [parentIdsWithChildren, rowsByCatalogId]);

  const additionalItemRowMap = useMemo(() => {
    const map = new Map<string, IssueAdditionalItemRow>();
    addableItemRows.forEach((row) => {
      map.set(row.groupId, row);
    });
    return map;
  }, [addableItemRows]);

  const availabilityMap = useMemo<Record<string, number>>(() => {
    return addableItemRows.reduce<Record<string, number>>((acc, row) => {
      acc[row.groupId] = row.availableQty;
      return acc;
    }, {});
  }, [addableItemRows]);

  const toggleParentExpansion = useCallback((groupId: string) => {
    setExpandedParentIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  const displayedAdditionalItemRows = useMemo<IssueAdditionalItemDisplayRow[]>(() => {
    return buildAdditionalItemsDisplayRows({
      rowsByCatalogId,
      catalogChildren: allChildren,
      expandedParentIds,
      search: debouncedSearch,
      availabilityFilter,
    });
  }, [allChildren, availabilityFilter, debouncedSearch, expandedParentIds, rowsByCatalogId]);

  const handleAddAdditionalItem = useCallback(
    async (row: IssueAdditionalItemRow) => {
      if (readOnly) return;
      await allocateAssets({
        catalogId: row.groupId,
        itemName: row.name,
        quantity: 1,
        sizeLabel: row.sizeLabel,
        locationId: resolveLocationForGroupId(row.groupId),
      });
    },
    [allocateAssets, resolveLocationForGroupId, readOnly],
  );

  const handleRemoveAdditionalItem = useCallback(
    async (groupId: string) => {
      if (readOnly) return;
      // Find the cart item to get its asset IDs
      const cartItem = additionalItems.find((item) => item.groupId === groupId);
      if (!cartItem || cartItem.assetIds.length === 0) return;
      await dbCart.removeFromCart(cartItem.assetIds);
    },
    [dbCart, additionalItems, readOnly],
  );

  const handleAdjustAdditionalQuantity = useCallback(
    async (groupId: string, delta: number) => {
      if (readOnly) return;
      if (delta === 0) {
        return;
      }

      if (delta > 0) {
        const row = additionalItemRowMap.get(groupId);

        if (!row) {
          notifications.warning(
            'Unable to add item',
            'This additional item is no longer available in inventory.',
          );
          return;
        }

        await handleAddAdditionalItem(row);
        return;
      }

      // For decrementing, we need to remove assets from the cart item
      const cartItem = additionalItems.find((item) => item.groupId === groupId);
      if (!cartItem) return;

      const assetsToRemove = Math.abs(delta);
      if (assetsToRemove >= cartItem.assetIds.length) {
        // Remove all assets for this group
        await dbCart.removeFromCart(cartItem.assetIds);
      } else {
        // Remove specific assets from the end
        const assetsToRemoveIds = cartItem.assetIds.slice(-assetsToRemove);
        await dbCart.removeFromCart(assetsToRemoveIds);
      }
    },
    [
      additionalItemRowMap,
      additionalItems,
      handleAddAdditionalItem,
      notifications,
      dbCart,
      readOnly,
    ],
  );

  const content = useMemo(() => {
    return (
      <IssueAdditionalItemsTable
        isLoading={isLoading || isCatalogChildrenLoading}
        isLoadingMore={isLoadingMore}
        rows={displayedAdditionalItemRows}
        onAddItem={handleAddAdditionalItem}
        onToggleExpand={toggleParentExpansion}
        readOnly={readOnly}
        locationOptionsByGroupId={locationOptionsByCatalogId}
        selectedLocationByGroupId={selectedLocationByGroupId}
        onLocationChange={handleLocationChange}
        locationsLoading={isLocationsLoading}
      />
    );
  }, [
    displayedAdditionalItemRows,
    handleAddAdditionalItem,
    isCatalogChildrenLoading,
    isLoading,
    isLoadingMore,
    isLocationsLoading,
    readOnly,
    toggleParentExpansion,
    locationOptionsByCatalogId,
    selectedLocationByGroupId,
    handleLocationChange,
  ]);

  const toolbar = useMemo(() => {
    return (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="subtitle2">Additional Inventory</Typography>
          <Typography variant="body2" color="text.secondary">
            Search available groupings and add equipment as needed.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 150 }} disabled={isLoading}>
            <InputLabel id="availability-filter-label">Availability</InputLabel>
            <Select
              labelId="availability-filter-label"
              label="Availability"
              value={availabilityFilter}
              onChange={(event) =>
                setAvailabilityFilter(event.target.value as AvailabilityFilter)
              }
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="available">Available</MenuItem>
              <MenuItem value="unavailable">Unavailable</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Search Catalog Items to Add"
            placeholder="Search by nomenclature, LIN, or NSN"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ width: { xs: '100%', sm: 280 } }}
            disabled={isLoading}
          />
        </Stack>
      </Stack>
    );
  }, [availabilityFilter, isLoading, search]);

  const actions = useMemo<IssueAdditionalItemsTabActions>(
    () => ({
      adjustQuantity: handleAdjustAdditionalQuantity,
      removeItem: handleRemoveAdditionalItem,
      clearCart: () => {
        dbCart.clearCart();
      },
    }),
    [handleAdjustAdditionalQuantity, handleRemoveAdditionalItem, dbCart],
  );

  const prevReservedAssetIdsRef = useRef<string[]>([]);
  useEffect(() => {
    const prev = prevReservedAssetIdsRef.current;
    const changed =
      prev.length !== reservedAssetIds.length || reservedAssetIds.some((id, i) => prev[i] !== id);
    if (changed) {
      prevReservedAssetIdsRef.current = reservedAssetIds;
      onReservedAssetIdsChange?.(reservedAssetIds);
    }
  }, [onReservedAssetIdsChange, reservedAssetIds]);

  return {
    toolbar,
    content,
    cartItems: additionalItemsWithLocation,
    availabilityMap,
    actions,
    reservedAssetIds,
  };
};
